import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Fingerprint } from "lucide-react";
import { AuthFlowError, describeAuthError, reauthenticate } from "../app/authFlow.js";
import type { SecondFactorOutcome } from "../api/auth.js";
import { useLockVault } from "../app/lockVault.js";
import { passkeyUnlockAvailable, reconfirmWithPasskey } from "../app/passkeyUnlock.js";
import { useSession } from "../app/session.js";
import { SecondFactorStep } from "../features/auth/SecondFactorStep.js";
import { Dialog } from "../ui/Dialog.js";
import { Alert, Button } from "../ui/primitives.js";
import { Field, SecretInput } from "../ui/forms.js";
import { useToast } from "../ui/Toast.js";

/** Shown when the server rejects the session. Re-proves the master password without reloading keys. */
export function ReauthDialog(): ReactNode {
  const { state, resume } = useSession();
  const lock = useLockVault();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [ticket, setTicket] = useState<string | undefined>(undefined);

  if (state.status !== "expired") {
    return null;
  }
  const { email, client } = state;

  function finish(): void {
    resume();
    void queryClient.invalidateQueries();
    setPassword("");
    setTicket(undefined);
    setError(undefined);
    toast.notify("Sitzung erneuert.", { tone: "success" });
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || password === "") {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const step = await reauthenticate(client, email, password);
      if (step.kind === "authenticated") {
        finish();
      } else if (step.kind === "second-factor" && !step.passwordChange) {
        setTicket(step.ticket);
      } else {
        setError("Dieses Konto muss zuerst ein neues Master-Passwort festlegen. Bitte sperre den Tresor und melde dich neu an.");
      }
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
      setPassword("");
    }
  }

  async function confirmWithPasskey(): Promise<void> {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await reconfirmWithPasskey(email);
      finish();
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={(): void => undefined}
      dismissible={false}
      title="Sitzung bestätigen"
      description={`Aus Sicherheitsgründen endet deine Sitzung nach einer Weile. Bestätige dein Master-Passwort für ${email}, um weiterzuarbeiten. Deine Einträge bleiben geöffnet.`}
      footer={
        <div className="flex justify-between gap-3">
          <Button variant="ghost" onClick={(): void => void lock("manual")}>
            Tresor sperren
          </Button>
        </div>
      }
    >
      {ticket !== undefined ? (
        <div className="p-5">
          <SecondFactorStep
            ticket={ticket}
            onVerified={async (outcome: SecondFactorOutcome): Promise<void> => {
              if (outcome.kind !== "session") {
                throw new AuthFlowError("incomplete", "Dieses Konto muss zuerst ein neues Master-Passwort festlegen. Bitte sperre den Tresor und melde dich neu an.");
              }
              finish();
            }}
          />
        </div>
      ) : (
        <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 p-5">
          <Field id="reauth-password" label="Master-Passwort">
            <SecretInput
              id="reauth-password"
              data-autofocus
              autoComplete="current-password"
              value={password}
              onChange={(event): void => setPassword(event.target.value)}
              revealLabel="Master-Passwort"
            />
          </Field>
          {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
          <Button type="submit" loading={busy} disabled={password === ""} className="w-full" size="lg">
            {busy ? "Wird geprüft …" : "Fortfahren"}
          </Button>
          {passkeyUnlockAvailable() ? (
            <Button variant="secondary" icon={Fingerprint} disabled={busy} className="w-full" onClick={(): void => void confirmWithPasskey()}>
              Mit Passkey bestätigen
            </Button>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}
